import type { SwadeItemContainerInterface, SwadeItemDetailInterface } from "@/types/items";
import type { SwadePowerContainerInterface, SwadePowerDetailOverridesInterface } from "@/types/powers";
import { coerceContainerValue, resolveContainerField } from "@/appdata/container-fields";

/**
 * A container's details after its overrides are applied.
 *
 * Partial, not the full interface: SwadeCyberwareDetailInterface.strain is required, and a merged
 * object cannot guarantee it. Call sites are unaffected — reading through `?.` already yields
 * `T | undefined` today, and every site pairs it with a `??` fallback.
 */
export type ResolvedItemDetails = Partial<NonNullable<SwadeItemDetailInterface>>;

/**
 * Overrides applied over a base: shallow, override-wins-per-key, null-valued overrides dropped, and
 * the BASE handed back by reference when nothing applies. Mirrors ItemContainer::getResolvedDetails()
 * in the API — both drop null-valued overrides themselves rather than relying on the server-side
 * filter, because that filter runs on persist and the rebuild writes this blob client-side, so
 * between a write and a save it legitimately holds `{ damage: null }` meaning "no override".
 * `resolveContainerDetails` and `resolvePowerDetails` are this one rule over their two bases.
 */
export function mergeOverrides<T extends object>(base: T | null, overrides: unknown): T | null {
    // Mirror of the PHP resolver's `is_array` guard. `configuration` is decoded JSON, so a
    // malformed payload can carry a string or array here; `Object.entries('abc')` would box it
    // into character entries and pollute the merge instead of falling back.
    if (overrides !== null && overrides !== undefined && (typeof overrides !== 'object' || Array.isArray(overrides))) {
        return base;
    }
    const applied = Object.fromEntries(
        Object.entries((overrides as Record<string, unknown> | null | undefined) ?? {})
            .filter(([, value]) => value !== null && value !== undefined),
    );
    // Test emptiness AFTER filtering: `{}` and `{ damage: null }` both mean "no effective override",
    // and both hand back the base object BY REFERENCE — callers rely on getting the object they read.
    if (Object.keys(applied).length === 0) return base;
    return { ...(base ?? {}), ...applied } as T;
}

/** The selection's details with the container's overrides applied. */
export function resolveContainerDetails(
    container: SwadeItemContainerInterface | null | undefined,
): ResolvedItemDetails | null {
    return mergeOverrides<ResolvedItemDetails>(container?.selection?.details ?? null, container?.configuration?.details ?? null);
}

/**
 * A power's stat line: the details a container.powers modification may change — the override
 * vocabulary itself, so the base, the override and the strategies cannot drift apart.
 */
export type PowerDetailBase = SwadePowerDetailOverridesInterface;

/**
 * A power container's stat line BEFORE overrides (spec 2026-09-11 §3.1): the single definition both
 * the override fold and the readers consume. `powerPoints` is the live pre-override total the store
 * keeps in `power.powerPoints` (null for a textual cost); range, duration and damage read the level
 * copies before the catalog, exactly as both cards do; rank is the catalog's (a superpower has none);
 * ap is null until a container.powers override sets it (2026-09-23).
 */
export function powerDetailBase(power: SwadePowerContainerInterface | null | undefined): PowerDetailBase {
    const configuration = (power?.configuration ?? null) as Record<string, unknown> | null;
    const details = (power?.selection?.details ?? null) as Record<string, unknown> | null;
    const own = (source: Record<string, unknown> | null, key: string): unknown =>
        source !== null && Object.prototype.hasOwnProperty.call(source, key) ? source[key] : null;
    return {
        powerPoints: (power?.powerPoints ?? null) as number | null,
        range: (own(configuration, 'range') ?? own(details, 'range') ?? null) as string | null,
        duration: (own(configuration, 'duration') ?? own(details, 'duration') ?? null) as number | string | null,
        damage: (own(configuration, 'damage') ?? own(details, 'damage') ?? null) as string | null,
        rank: (own(details, 'rank') ?? null) as string | null,
        // No catalog or level copy carries armor piercing; read like the rest so an authored one would count.
        ap: (own(configuration, 'ap') ?? own(details, 'ap') ?? null) as number | null,
    };
}

/** The stat line the cards and the build tracker read: the base with `configuration.details` applied. */
export function resolvePowerDetails(power: SwadePowerContainerInterface | null | undefined): PowerDetailBase {
    return mergeOverrides<PowerDetailBase>(powerDetailBase(power), power?.configuration?.details ?? null) as PowerDetailBase;
}

/**
 * The trappings a card shows: the `trapping` override, else the player's own text. The override
 * key is the shared vocabulary every path uses; the container's own field is `trappings`, which
 * is why resolveContainerField's own-key fallback finds nothing and this reader supplies it.
 */
export function resolvePowerTrappings(power: SwadePowerContainerInterface | null | undefined): string | null {
    return coerceContainerValue(resolveContainerField(power, 'trapping')) ?? power?.trappings ?? null;
}

/**
 * The name a card shows: the `name` override, else the player's own name, else the catalog name —
 * with the rule both cards applied inline before this helper existed: a name counts only when its
 * length is greater than zero, so a cleared edit input (`''`) falls back to the catalog while a
 * whitespace-only name still shows as is. `resolveContainerField` already folds the override over
 * the container's own `name` key, so the length test runs on whichever of the two it returned.
 */
export function resolvePowerDisplayName(power: SwadePowerContainerInterface | null | undefined): string | null {
    const name = coerceContainerValue(resolveContainerField(power, 'name'));
    return (name?.length ?? 0) > 0 ? name : power?.selection?.name ?? null;
}
